/**
 * PER-ENDPOINT CACHE KEYS — the reason a filter change does not refetch the
 * whole screen.
 *
 * Every request this screen makes consumes a SUBSET of the filter. The analytics
 * batch does not care about `priority`; the records query does not care about
 * `measure`; nothing at all cares about `tab`, `density` or which legend series
 * are hidden. So each endpoint declares the fields it actually reads, and its
 * cache key is the serialization of ONLY those fields.
 *
 * The consequences are the whole point:
 *
 *   · changing a Records-only criterion leaves every chart alone,
 *   · switching tabs refetches NOTHING that is already loaded,
 *   · toggling a legend series, changing density or opening a column menu costs
 *     zero requests,
 *   · and the request budget (data/request-budget.ts) has far less to do,
 *     which matters because production runs a connection pool of five and every
 *     one of these routes holds one for the life of the request.
 *
 * The analytics screen had to discover a narrow version of this the hard way:
 * its `state` memo is keyed on five scope parameters rather than on the
 * URLSearchParams object precisely so that changing the dwell subject does not
 * re-run the overview, the batch and all four hub panels
 * (dashboard-analytics.tsx:227-235). This generalises it.
 *
 * ── ADDING A FIELD ──
 * A new filter field that an endpoint reads MUST be added to that endpoint's
 * list here, or the screen will show a stale answer for it with complete
 * confidence. `filter-slices.test.ts` asserts that every field in the model is
 * either claimed by at least one slice or listed in VIEW_ONLY below, so a field
 * cannot be added and quietly forgotten.
 */
import { DashboardFilter } from './filter-model';

export type SliceName =
  | 'meta'
  | 'overview'
  | 'batch'
  | 'latency'
  | 'quality'
  | 'qualityDrill'
  | 'turnover'
  | 'rhythm'
  | 'activity'
  | 'records'
  | 'corrections'
  | 'logs'
  /* COMPARISON MODE. The same two endpoints again, for the SECOND scope. Two
     more slices rather than a second copy of the hook: they then inherit the
     cache, the abort-on-change, the retry and — the one that matters — the
     semaphore, so the second scope queues BEHIND the first instead of doubling
     the concurrent load against a pool of five. */
  | 'overviewB'
  | 'batchB';

type Field = keyof DashboardFilter;

/** Fields that drive no request at all. They travel in the URL and in a saved
 *  view, and they cost nothing. */
export const VIEW_ONLY: Field[] = [
  'tab',
  'seriesOff',
  'density',
  'columns',
  'sort',
  'drill',
  // `role` USED TO BE HERE, on the grounds that /activity filters by actor id
  // and never by role. That is still true of /activity — but scopeQuery DOES
  // send `role` to every analytics route (dashboard-data.ts), so it is part of
  // the question those routes are asked and belongs in their key. A field that
  // is on the wire and not in the key is precisely the stale-answer bug.
  // `granularity` selects WHICH panel ids the batch plan asks for rather than
  // changing the request's scope; the plan builder reads it and the batch key
  // below includes it, so it is claimed there. Listed nowhere else.
];

/**
 * EVERY FIELD `scopeQuery` PUTS ON THE WIRE — the analytics slices' shared key.
 *
 * THIS LIST IS NOT A CHOICE. It is the set of fields
 * `data/dashboard-data.ts#scopeQuery` actually sends, and it is asserted against
 * that function's real output in `dashboard-data.test.ts` — build two filters
 * differing in one field, call scopeQuery on both, and if the bodies differ the
 * field MUST be in here.
 *
 * WHY IT EXISTS. The slices used to name six to ten fields each by hand, while
 * scopeQuery sent twenty-nine. The twenty-one in the gap were sent but were not
 * part of the cache key, so setting one — Unit 4B, Kind = Guest, 09:00–17:00 —
 * left every slice's key unchanged, fired NO request, and served the previous
 * answer under a chip row that said otherwise. Clearing the filter did the same
 * in reverse and kept the narrowed figures. Both numbers looked right.
 *
 * That is the exact failure the header of this file warns about ("the screen
 * will show a stale answer for it with complete confidence"), reached by the
 * mechanism it warned about: a field added to the request and forgotten here.
 * The only durable fix is to stop maintaining the two lists separately.
 *
 * A SLICE STILL DECLARES ITS OWN AXES on top of this — `measure`, `granularity`,
 * `subject`, `turnoverBucket`, `qualityDrill` — because those are not part of
 * the shared scope and changing one must refetch only the panel that reads it.
 */
export const ANALYTICS_SCOPE_FIELDS: Field[] = [
  'range',
  'from',
  'to',
  'compare',
  'buildingId',
  'portfolioId',
  'kinds',
  'severity',
  'visitorDetails',
  'unit',
  'actors',
  'role',
  'approval',
  'priority',
  'preAuthorized',
  'onPass',
  'parkingValidation',
  'numberOfGuests',
  'carrier',
  'parcelStatus',
  'status',
  'direction',
  'groups',
  'visitorType',
  'buildingActivity',
  'timeFrom',
  'timeTo',
  'q',
  'name',
  'lastName',
];

/* The comparison scope's version: the same window and the same narrowing, a
   different place. `compareScope` REPLACES buildingId/portfolioId, exactly as
   `compareScopeQuery` overrides them. */
const COMPARE_SCOPE_FIELDS: Field[] = [
  'compareScope',
  ...ANALYTICS_SCOPE_FIELDS.filter(
    field => field !== 'buildingId' && field !== 'portfolioId'
  ),
];

export const SLICE_FIELDS: Record<SliceName, Field[]> = {
  /* Fetched once per session. It describes the CALLER, not the query. */
  meta: [],

  /* countWindow over one window. No measure, no dimension — it returns every
     figure the read model knows about. */
  overview: [...ANALYTICS_SCOPE_FIELDS],

  /* Every breakdown panel, in one request. `granularity` is here because it
     decides whether the pulse asks for `day` or `week`; `kinds` and `severity`
     are panel-level filters the route accepts and nothing used to send. */
  batch: [...ANALYTICS_SCOPE_FIELDS, 'measure', 'granularity'],

  /* ── the comparison scope's copies ──
     Every field the primary reads EXCEPT the scope, which comes from
     `compareScope`. So changing the period refetches both, and changing the
     second scope refetches only the second — which is what makes picking a
     comparison building cost one request rather than a screenful. */
  overviewB: [...COMPARE_SCOPE_FIELDS],
  batchB: [...COMPARE_SCOPE_FIELDS, 'measure', 'granularity'],

  /* The dwell panel. `subject` is its own axis — changing it must refetch THIS
     and nothing else, which is why the old screen gave it an effect of its own
     (dashboard-analytics.tsx:566-580). */
  latency: [...ANALYTICS_SCOPE_FIELDS, 'subject'],

  /* Six ratios about the record. Takes no measure and no kind filter. */
  quality: [...ANALYTICS_SCOPE_FIELDS],

  /* The rows behind ONE ratio. Its own slice because it is the one request on
     the screen whose cost is per click. */
  qualityDrill: [...ANALYTICS_SCOPE_FIELDS, 'qualityDrill'],

  turnover: [...ANALYTICS_SCOPE_FIELDS, 'turnoverBucket'],

  rhythm: [...ANALYTICS_SCOPE_FIELDS, 'measure'],

  /* The feed. No portfolio — /activity takes a single buildingId and RLS bounds
     the rest, so a portfolio narrowing is applied client-side by the building
     set it resolves to (and, when it cannot be, the panel says so). */
  activity: [
    'buildingId',
    'range',
    'from',
    'to',
    'groups',
    'kinds',
    'actors',
    'severity',
    'direction',
    'status',
    'unit',
    'name',
    'q',
    'page',
  ],

  /* POST /dashboard/reports. Note `book` first: it selects the TABLE. */
  records: [
    'book',
    'buildingId',
    'from',
    'to',
    'range',
    'timeFrom',
    'timeTo',
    'unit',
    'visitorType',
    'visitorDetails',
    'preAuthorized',
    'onPass',
    'approval',
    'parkingValidation',
    'numberOfGuests',
    'buildingActivity',
    'priority',
    'carrier',
    'parcelStatus',
    'name',
    'lastName',
    'page',
  ],

  corrections: ['book', 'page'],

  /* GET /logs — one building, one desk day. The Reports tab's day view. */
  logs: ['buildingId', 'from'],
};

const pick = (filter: DashboardFilter, fields: Field[]): unknown[] =>
  fields.map(field => filter[field]);

/**
 * The cache key for one endpoint under one filter.
 *
 * Deterministic and order-stable: it serialises an ARRAY in the declared field
 * order rather than an object, so it cannot depend on key iteration order and
 * cannot change because somebody re-sorted the list above alphabetically.
 */
export const sliceKey = (
  name: SliceName,
  filter: DashboardFilter
): string => `${name}|${JSON.stringify(pick(filter, SLICE_FIELDS[name]))}`;

/** Did this endpoint's question change between two filters? */
export const sliceChanged = (
  name: SliceName,
  a: DashboardFilter,
  b: DashboardFilter
): boolean => sliceKey(name, a) !== sliceKey(name, b);

/**
 * Which slices a tab needs.
 *
 * Overview deliberately claims `latency` and `quality`: it prints the dwell p50
 * and two of the six quality ratios as headlines, and a headline whose panel has
 * not loaded is a headline that says "—" on a screen that has finished loading.
 * Everything else is loaded on the tab that owns it and then cached, so a second
 * visit is instant.
 */
export const SLICES_FOR_TAB: Record<string, SliceName[]> = {
  overview: [
    'meta',
    'overview',
    'batch',
    'latency',
    'quality',
    'activity',
    'overviewB',
    'batchB',
  ],
  trends: [
    'meta',
    'overview',
    'batch',
    'latency',
    'quality',
    'qualityDrill',
    'turnover',
    'rhythm',
    'overviewB',
    'batchB',
  ],
  /* `overview` IS ON EVERY TAB, and that is not padding.

     The KPI strip is sticky: it sits ABOVE the tab bar and is on screen
     whichever tab is open. It was fetched only on the tabs that draw panels, so
     narrowing the filter from the Records tab left the strip showing the
     figures for the PREVIOUS filter — org-wide totals under a chip row that
     said "Aurora Tower", on a screen that had finished loading and reported no
     error. That is the exact class of quiet dishonesty this screen exists to
     remove, and it was found by the browser suite rather than by 864 unit
     tests.

     It is one request, keyed like every other slice, so a tab switch that does
     not change the filter still costs nothing. */
  activity: ['meta', 'overview', 'activity', 'corrections'],
  records: ['meta', 'overview', 'records'],
  reports: ['meta', 'overview', 'batch', 'records', 'logs', 'overviewB', 'batchB'],
};

/**
 * Load order within a tab. `meta` first because nothing can be requested without
 * it — each measure carries its own legal dimension list, and sending a pair
 * outside it makes the server substitute one and say so, which is a chart with
 * the wrong title. Then the two that fill the page above the fold, then the rest.
 */
export const SLICE_PRIORITY: Record<SliceName, number> = {
  meta: 0,
  overview: 1,
  batch: 1,
  records: 2,
  activity: 3,
  latency: 4,
  quality: 4,
  turnover: 5,
  rhythm: 5,
  logs: 5,
  corrections: 6,
  qualityDrill: 6,
  /* LAST, ALWAYS. The primary scope is the answer; the comparison is context
     for it. A reader waiting on their own building's figures because a second
     building's request went first would be a worse screen than one without
     comparison mode at all. */
  overviewB: 7,
  batchB: 7,
};
